import type { WorkflowDefinition } from "../types";

/**
 * W30 — Turn a category map into prospects.
 *
 * Property-scoped, on the category W29 mapped: the ranking it reads belongs to
 * that market, and there is no version of this playbook that works without one.
 * Gated on the same plan predicate the prospects API enforces, so it is never
 * offered to an organization that would be refused at the third step.
 *
 * Three of its four steps are marked done by hand on purpose. Which brands have
 * a gap worth pitching is a judgement call, and the prospect list belongs to the
 * whole organization: a count over it would tick this playbook off the moment a
 * colleague added somebody for a different market last month.
 */
export const categoryProspectsWorkflow: WorkflowDefinition = {
	id: "category-prospects",
	wNumber: 30,
	title: "Turn a category map into prospects",
	outcome:
		"Find the brands buyers know that AI rarely names, and walk into the pitch with their numbers.",
	audiences: ["agencies", "pr"],
	uses: ["Brands", "AI Reports", "Prospects"],
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	isApplicable: (mode, org) => mode.isCategory && org.canProspect,
	articleSlug: "how-to-turn-a-category-map-into-prospects",
	articleStatus: "published",
	caveats: [
		"Prospecting comes with an Agency plan, and a trial does not include it.",
	],
	status: "live",
	steps: [
		{
			id: "rank-brands",
			title: "Rank the brands",
			page: "Brands",
			route: "/dashboard/competitors",
			criterion: "You've got at least five brands in the ranking.",
			why: "The ranking is share of voice across the whole market. It says which brands AI names, and how often it names them.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Read the ranking from the top down",
				"Check the share each brand holds",
				"Note how far the tail drops off",
			],
			complete: { kind: "count", key: "competitorCount", gte: 5 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Brands in the ranking",
							value: state.property.competitorCount,
							target: 5,
						}
					: null,
		},
		{
			id: "pick-gaps",
			title: "Pick the brands with a gap",
			page: "Brands",
			route: "/dashboard/competitors",
			criterion: "You've picked the brands worth approaching.",
			why: "A brand buyers know well that sits low in the ranking has a gap it can be shown. That gap is the pitch.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Shortlist the brands people in this market know well",
				"Keep the ones AI names least often",
				"Drop anyone who is already doing this work",
			],
			complete: { kind: "manual" },
		},
		{
			id: "add-prospect",
			title: "Add one as a prospect",
			page: "Brands",
			route: "/dashboard/competitors",
			criterion: "You've added the brand to your prospect list.",
			why: "A prospect sits in its own list, so a target brand never mixes into the brands you already track.",
			docsHref: "/docs/dashboards/prospecting",
			tasks: [
				"Copy the brand name and domain out of the ranking",
				"Add it on the organization dashboard's Prospects tab",
				"Keep the batch to the brands you can call this month",
			],
			complete: { kind: "manual" },
		},
		{
			id: "pitch",
			title: "Pitch with a report they can read",
			page: "Brands",
			route: "/dashboard/competitors",
			criterion: "You've sent a report to the brand.",
			why: "A report in your own branding turns the gap into something the brand can read before the call.",
			docsHref: "/docs/dashboards/prospecting",
			tasks: [
				"Start a report for the prospect and read it first",
				"Lead with one finding, not five",
				"Send the branded link or PDF ahead of the call",
			],
			complete: { kind: "manual" },
		},
	],
};
